/**
 * This file contains the NewMeasurementProvider component, which is an improved version of the MeasurementProvider component.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { InputMeasurement, InputMeasurementCollection, joinMeasurementCollections, MeasurementRequirement, MeasurementResult } from '../data/Measurements/Measurement';
import { getTask, getTaskData, Task, TaskData, TaskIdentifier } from '../data/Tasks/Task';
import { useFirebase } from './FirebaseProvider';

import { Map } from 'immutable';
import { doc, DocumentReference, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { ProjectId } from '../data/Project';
import { ImageId } from '../data/ImageReference';
import { computeResults } from '../data/Measurements/Resolver';
import { LineMeasurementInput } from '../measurements/measurements';
import { orderMeasurementRequirementsByLead } from '../data/Utils';

/**
 * The name of the collection in Firestore that stores the measurements.
 */
const COLLECTION_NAME = 'measurements';

/**
 * The context for the new measurement provider.
 */
export type MeasurementProviderContextType = {
    /**
     * The task that the user is working on.
     */
    task: Task;

    /**
     * The data for the task that the user is working on.
     */
    taskData: TaskData;

    /**
     * Whether the context is ready to be used.
     */
    isReady: boolean;

    /**
     * The measurements that the user has input.
     */
    measurements: InputMeasurementCollection;

    /**
     * All the drawable measurements.
     */
    drawableMeasurements: InputMeasurement[];

    /**
     * The next measurement that the user should input, if any.
     */
    nextMeasurement: MeasurementRequirement | undefined;

    /**
     * The computed results for the measurements.
     */
    results: MeasurementResult[];

    /**
     * Updates the given measurement.
     * @param measurement The measurement to update.
     */
    updateMeasurement: (measurement: InputMeasurement) => void;

    /**
     * Updates the given measurement asynchronously.
     * @param measurement The measurement to update.
     */
    updateMeasurementAsync: (measurement: InputMeasurement) => Promise<void>;

    /**
     * Deletes the given measurement.
     * @param key The key of the measurement to delete.
     */
    deleteMeasurement: (key: string) => void;

    /**
     * Deletes all measurements.
     */
    deleteAllMeasurements: () => void;

    /**
     * Undoes the last measurement change.
     */
    undoLastMeasurementChange: () => void;

    /**
     * Sets the calibration for the image.
     * @param calibration The calibration to set.
     */
    setCalibration: (calibration: LineMeasurementInput | null) => void;

    /**
     * Whether the image is calibrated.
     */
    isCalibrated: boolean;
};

export const MeasurementContext = createContext<MeasurementProviderContextType | undefined>(undefined);

/**
 * The type of the database object that stores the measurements.
 */
type MeasurementDatabaseObjectType = {
    measurements: Record<string, InputMeasurement>;
    calibration: LineMeasurementInput | null;
    lastEdited: Date;
}

/**
 * The provider for the new measurement system.
 */
export const NewMeasurementProvider: React.FC<{ children: React.ReactNode, taskIdentifier: TaskIdentifier, projectId: ProjectId, imageId: ImageId }> = ({ children, taskIdentifier, projectId, imageId }) => {
    const { db, user } = useFirebase();

    const task = getTask(taskIdentifier);
    const taskData = getTaskData(taskIdentifier);

    const [isReady, setIsReady] = useState<boolean>(false);

    const [databaseObject, setDatabaseObject] = useState<MeasurementDatabaseObjectType>({
        measurements: {},
        calibration: null,
        lastEdited: new Date()
    });

    const [measurementHistory, setMeasurementHistory] = useState<Array<InputMeasurementCollection>>([]);

    const measurements = useMemo(() => {
        return Map(databaseObject.measurements);
    }, [databaseObject]);

    const calibration = useMemo(() => {
        return databaseObject.calibration;
    }, [databaseObject]);

    const isCalibrated = useMemo(() => {
        return calibration !== null;
    }, [calibration]);

    // MARK: Firestore sync

    const [documentRef, setDocumentRef] = useState<DocumentReference | null>(null);

    // Effect #1: Secure and set up the document reference for the measurements.
    useEffect(() => {
        if (!user) {
            throw new Error('User is not signed in.');
        }

        const documentRef = doc(db, COLLECTION_NAME, user.uid, projectId, 'tasks', taskIdentifier, imageId);

        getDoc(documentRef)
            .then(doc => {
                if (doc.exists()) {
                    console.log(`Found document for ${projectId}/${imageId} in`, doc.ref.path);

                    setDocumentRef(documentRef);
                } else {
                    console.log(`Creating an empty document for ${projectId}/${imageId} in`, doc.ref.path);

                    setDocumentRef(documentRef);

                    setDoc(documentRef, {
                        measurements: {},
                        calibration: null,
                        lastEdited: new Date()
                    } as MeasurementDatabaseObjectType);
                }

                setIsReady(true);
            })
            .catch(error => {
                console.error(`Failed to get document for ${projectId}/${imageId}:`, error);

                setIsReady(false);
            })
    }, [db, imageId, projectId, user]);

    // Effect #2: Watch for changes in the document to update the local state.
    useEffect(() => {
        if (!documentRef) {
            return;
        }

        const unsubscribe = onSnapshot(documentRef, (doc) => {
            if (doc.exists()) {
                const data = doc.data() as MeasurementDatabaseObjectType;

                console.log(`Received data for ${projectId}/${imageId}:`, data);

                setDatabaseObject(data);
            }
        });

        return () => {
            console.log('Unsubscribing from document changes.');

            unsubscribe();
        };
    }, [documentRef, imageId, projectId]);

    // MARK: Measurement editing

    const updateMeasurement = useCallback((input: InputMeasurement) => {
        updateMeasurementAsync(input)
            .then(() => {
                console.log(`Updated measurement ${input.key} in ${projectId}/${imageId}.`);
            })
            .catch(error => {
                console.error(`Failed to update measurement ${input.key} in ${projectId}/${imageId}:`, error);
            });
    }, [databaseObject, documentRef, imageId, measurements, projectId]);

    const updateMeasurementAsync = useCallback(async (input: InputMeasurement) => {
        if (!documentRef) {
            throw new Error('Document reference is not set.');
        }

        setMeasurementHistory([...measurementHistory, measurements]);

        const updated = measurements.set(input.key, input);
        const object = {
            ...databaseObject,
            measurements: updated.toObject(),
            lastEdited: new Date(),
        } as MeasurementDatabaseObjectType;

        try {
            await setDoc(documentRef, object);

            console.log(`Updated measurement ${input.key} in ${projectId}/${imageId}.`);
        } catch (error) {
            console.error(`Failed to update measurement ${input.key} in ${projectId}/${imageId}:`, error);
        }
    }, [databaseObject, documentRef, imageId, measurements, projectId, measurementHistory, setMeasurementHistory]);

    const deleteMeasurement = useCallback((key: string) => {
        if (!documentRef) {
            throw new Error('Document reference is not set.');
        }

        const updated = measurements.delete(key);
        const object = {
            ...databaseObject,
            measurements: updated.toObject(),
            lastEdited: new Date(),
        } as MeasurementDatabaseObjectType;

        setDoc(documentRef, object)
            .then(() => {
                console.log(`Deleted measurement ${key} in ${projectId}/${imageId}.`);
            })
            .catch(error => {
                console.error(`Failed to delete measurement ${key} in ${projectId}/${imageId}:`, error);
            });
    }, [databaseObject, documentRef, imageId, measurements, projectId]);

    const deleteAllMeasurements = useCallback(() => {
        if (!documentRef) {
            throw new Error('Document reference is not set.');
        }

        setDoc(documentRef, {
            ...databaseObject,
            measurements: {},
            lastEdited: new Date()
        })
            .then(() => {
                console.log(`Deleted all measurements in ${projectId}/${imageId}.`);
            })
            .catch(error => {
                console.error(`Failed to delete all measurements in ${projectId}/${imageId}:`, error);
            });
    }, [databaseObject, documentRef, imageId, projectId]);

    const undoLastMeasurementChange = useCallback(() => {
        if (measurementHistory.length === 0) {
            return;
        }

        const lastMeasurement = measurementHistory[measurementHistory.length - 1];

        setMeasurementHistory(measurementHistory.slice(0, measurementHistory.length - 1));

        if (!documentRef) {
            throw new Error('Document reference is not set.');
        }

        setDoc(documentRef, {
            ...databaseObject,
            measurements: lastMeasurement.toObject(),
            lastEdited: new Date()
        })
            .then(() => {
                console.log(`Undid last measurement change in ${projectId}/${imageId}.`);
            })
            .catch(error => {
                console.error(`Failed to undo last measurement change in ${projectId}/${imageId}:`, error);
            });
    }, [databaseObject, documentRef, imageId, measurementHistory, projectId]);

    const setCalibration = useMemo(() => {
        return (calibration: LineMeasurementInput | null) => {
            if (!documentRef) {
                throw new Error('Document reference is not set.');
            }

            setDoc(documentRef, {
                ...databaseObject,
                calibration,
                lastEdited: new Date()
            })
                .then(() => {
                    console.log(`Updated calibration in ${projectId}/${imageId}.`);
                })
                .catch(error => {
                    console.error(`Failed to update calibration in ${projectId}/${imageId}:`, error);
                });
        };
    }, [databaseObject, documentRef, imageId, projectId]);

    // MARK: Measurement computing

    const results = useMemo(() => {
        const collection = joinMeasurementCollections(taskData.derivedMeasurements, measurements);

        return computeResults(collection, true);
    }, [measurements, taskData]);

    const drawableMeasurements = useMemo(() => {
        return Array.from(measurements.values()).filter(m => m.drawable) as InputMeasurement[];
    }, [measurements]);

    // MARK: Other properties

    const nextMeasurement = useMemo(() => {
        // find the first requirement that is not in the measurements
        // const sortedRequirements = orderMeasurementRequirementsByLead(taskData.requirements);

        const missingRequirements = taskData.requirements.filter(req => !measurements.has(req.key));

        const missingRequirementsThatShouldNotBeSkiped = missingRequirements.filter(
            req => req.shouldSkipRequirement ? !req.shouldSkipRequirement({ inputs: measurements, results: results }) : true
        );

        return missingRequirementsThatShouldNotBeSkiped.length > 0 ? missingRequirementsThatShouldNotBeSkiped[0] : undefined;
    }, [measurements, results, taskData]);

    // MARK: Cleanup

    // MARK: UI

    return (
        <MeasurementContext.Provider value={{
            task,
            taskData,
            isReady,
            measurements,
            nextMeasurement,
            results,
            updateMeasurement,
            updateMeasurementAsync,
            deleteMeasurement,
            deleteAllMeasurements,
            undoLastMeasurementChange,
            drawableMeasurements,
            setCalibration,
            isCalibrated
        }}>
            {children}
        </MeasurementContext.Provider>
    );
}

/**
 * A hook to use the measurement context.
 */
export const useMeasurement = (): MeasurementProviderContextType => {
    const context = useContext(MeasurementContext);

    if (context === undefined) {
        throw new Error('useMeasurement must be used within a MeasurementProvider');
    }

    return context;
}